<template>
    <div
        class="flex flex-col h-full"
        :class="{
            'justify-between': !loading && !!contact?.id,
            'justify-center items-center': loading || loadingGroupSelection,
        }"
    >
        <template v-if="!canUseMessageHub">
            <div class="flex flex-col items-center gap-10 py-6 px-4">
                <vx-box
                    color="info"
                    class="w-full flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500 text-center"
                >
                    Message Hub is not available in this context.
                </vx-box>
                <voxie-logo />
            </div>
        </template>
        <template v-else-if="loading || loadingGroupSelection">
            <vx-logo-loading></vx-logo-loading>
        </template>
        <template v-else-if="ouGroupSelectionVisible">
            <conversation-title :hideEdit="true" @unloadContact="onGroupSelectionCancel" />
            <group-selection-screen
                :contactGroups="ouContactGroups"
                :selectedGroupId="ouSelectedGroupId"
                @select="onGroupSelected"
                @cancel="onGroupSelectionCancel"
                @update:selectedGroupId="ouSelectedGroupId = $event"
            />
        </template>
        <template v-else>
            <conversation-nav v-if="!contact?.id">
                <vx-button
                    v-if="FEATURES.contact_groups"
                    color="secondary"
                    size="sm"
                    data-test="context-switcher"
                    :aria-keyshortcuts="usesCommandKey ? 'Meta+G' : 'Control+G'"
                    @click="changeGroupModal = true"
                >
                    <navbar-context-label></navbar-context-label>
                </vx-button>
                <span v-else class="text-base font-extrabold text-slate-100">{{ currentTeam.name }}</span>
            </conversation-nav>
            <template v-if="contact?.id">
                <conversation-title
                    v-show="!contactDetailsOpened"
                    @open:contactDetails="contactDetailsOpened = true"
                    @unloadContact="handleUnloadContact"
                >
                </conversation-title>
                <div
                    v-show="!contactDetailsOpened"
                    class="flex flex-col h-full min-h-0 bg-white shadow-inner border-0 border-b border-solid border-slate-300"
                >
                    <conversation-messages></conversation-messages>
                </div>
                <conversation-actions v-show="!contactDetailsOpened"></conversation-actions>
                <conversation-details
                    v-if="contactDetailsOpened"
                    @close="contactDetailsOpened = false"
                    @currentGroupRemoved="unmount"
                ></conversation-details>
            </template>
            <template v-else>
                <div class="flex flex-col items-center gap-10 py-6 px-4">
                    <vx-box
                        color="info"
                        class="w-full flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500 text-center"
                    >
                        Select a number in your browser to get started.
                    </vx-box>
                    <voxie-logo />
                </div>
            </template>
        </template>

        <Teleport to="body">
            <Transition name="sheet">
                <div v-if="FEATURES.contact_groups && changeGroupModal" class="fixed inset-0 z-50">
                    <div class="fixed inset-0 bg-slate-300/80" @click="changeGroupModal = false"></div>
                    <div
                        class="fixed inset-x-0 inset-bs-16 inset-be-0 bg-white rounded-t-2xl shadow-xl flex flex-col overflow-hidden"
                    >
                        <div class="absolute z-20 right-4 top-4">
                            <vx-button color="muted" size="sm" @click="changeGroupModal = false" aria-label="Close">
                                <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                            </vx-button>
                        </div>

                        <div class="flex-1 min-h-0 overflow-y-auto">
                            <navbar-context-switch
                                :hideScroll="true"
                                :hideLinks="true"
                                @switching="loadingContact = true"
                            ></navbar-context-switch>
                        </div>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>
<script setup>
import VoxieLogo from '~/components/messages/browser-extension/components/misc/VoxieLogo.vue';
import ConversationTitle from '~/components/messages/browser-extension/components/ConversationTitle.vue';
import ConversationDetails from '~/components/messages/browser-extension/components/ConversationDetails.vue';
import ConversationActions from '~/components/messages/browser-extension/components/ConversationActions.vue';
import ConversationMessages from '~/components/messages/browser-extension/components/ConversationMessages.vue';
import NavbarContextSwitch from '~/components/navbar/NavbarContextSwitch.vue';
import NavbarContextLabel from '~/components/navbar/NavbarContextLabel.vue';
import { useEventListener, useTitle, useUrlSearchParams } from '@vueuse/core';
import { onMounted, ref, inject, computed } from 'vue';
import { actionsClient, contactsClient, groupContactsClient } from '~/services';
import { hasOpenModal } from '~/utils/dom';
import { e164Format, isValidPhoneNumber } from '~/utils/phoneNumber';
import { VxLogoLoading, VxButton, VxBox } from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ConversationNav from '~/components/messages/browser-extension/components/ConversationNav.vue';
import GroupSelectionScreen from '~/components/messages/browser-extension/components/GroupSelectionScreen.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { catchRequestError } from '~/utils/request';
import { useMessageHub } from '~/components/message-hub/useMessageHub';
import { useConversation } from '~/composables/useConversation';
import axios from '~/services/axios';

const $toasted = useToasted();
const FEATURES = inject('FEATURES');

const queryParams = useUrlSearchParams();
const currentTeam = Spark.state.currentTeam;
const teamId = Spark.state.currentTeam.id;

const { currentGroup, getCurrentGroupId, currentOrgUnit, canUseMessageHub } = useCurrentGroup();
const { loading: loadingContact, contact, prefill: prefillContact, unmount } = useContactDetails();
const { mount: mountConversation } = useConversation();
const { mountMessageHub } = useMessageHub();

const contextName = computed(() => {
    if (!FEATURES.contact_groups) {
        return '';
    }

    if (currentOrgUnit.value) {
        return currentOrgUnit.value.name;
    }

    if (currentGroup.value) {
        return currentGroup.value.name;
    }

    return 'Corporate';
});

const browserTitle = computed(() => {
    let title = contextName.value ? ` • ${contextName.value}` : '';

    title += ` • ${currentTeam.name}`;
    return title;
});

useTitle(computed(() => `Voxie ${browserTitle.value}`));

const contactDetailsOpened = ref(false);
const changeGroupModal = ref(false);

const ouGroupSelectionVisible = ref(false);
const ouContactId = ref(null);
const ouContactGroups = ref([]);
const ouSelectedGroupId = ref(null);
const loadingGroupSelection = ref(false);

const usesCommandKey = /iPad|iPhone|Mac OS/.test(navigator.userAgent);

useEventListener(window, 'keydown', (event) => {
    if ((event.metaKey && event.key === 'g') || (event.ctrlKey && event.key === 'g')) {
        event.preventDefault();
        // It would open out of sight behind the modal.
        if (FEATURES.contact_groups && !hasOpenModal()) {
            changeGroupModal.value = !changeGroupModal.value;
        }
    }
});

const loading = computed(() => searchingContact.value || loadingContact.value);

const searchingContact = ref(false);

const searchCancelKey = Symbol('browser-extension-search-contact');
const searchContact = async (params) => {
    if (currentGroup.value?.group_id) {
        return groupContactsClient.groupContactsList(teamId, currentGroup.value.group_id, params, {
            cancelKey: searchCancelKey,
        });
    } else {
        return contactsClient.searchContacts(teamId, params, {
            cancelKey: searchCancelKey,
        });
    }
};

const createContact = async (phoneNumber) => {
    if (currentGroup.value?.group_id) {
        return groupContactsClient.groupContactSave(
            teamId,
            currentGroup.value.group_id,
            { phone: phoneNumber },
            { cancelKey: searchCancelKey }
        );
    } else {
        return contactsClient.contactSave(teamId, { phone: phoneNumber }, { cancelKey: searchCancelKey });
    }
};

const loadContact = async (phoneNumber) => {
    contactDetailsOpened.value = false;
    ouGroupSelectionVisible.value = false;
    ouContactGroups.value = [];
    ouSelectedGroupId.value = null;

    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        searchingContact.value = false;
        return;
    }

    searchingContact.value = true;

    try {
        const params = {
            'filter[phone]': phoneNumber,
        };

        const searchResponse = await searchContact(params);

        let contactData;
        if (
            searchResponse.data?.data &&
            searchResponse.data?.data?.[0]?.id &&
            searchResponse.data?.data?.[0].phone === phoneNumber
        ) {
            contactData = searchResponse.data.data[0];
        } else {
            const createResponse = await createContact(phoneNumber);
            contactData = createResponse.data;
            actionsClient.send('contact_added');
        }

        if (currentOrgUnit.value?.id) {
            ouContactId.value = contactData.id;
            try {
                const groupsResponse = await contactsClient.listContactGroups(teamId, contactData.id);
                ouContactGroups.value = groupsResponse.data.data;
            } catch {
                ouContactGroups.value = [];
            }
            prefillContact(contactData.id, undefined, {
                team_id: teamId,
                ...contactData,
            });
            searchingContact.value = false;
            ouGroupSelectionVisible.value = true;
            return;
        }

        prefillContact(contactData.id, getCurrentGroupId(), {
            team_id: teamId,
            group_id: getCurrentGroupId(),
            ...contactData,
        });
        await mountConversation();

        searchingContact.value = false;
    } catch (e) {
        searchingContact.value = false;
        if (!axios.isCancel(e)) {
            $toasted.error(catchRequestError(e));
        }
    }
};

const onGroupSelected = async ({ groupId }) => {
    loadingGroupSelection.value = true;
    ouGroupSelectionVisible.value = false;

    try {
        const contactInGroup = ouContactGroups.value.some((cg) => cg.group_id === groupId);
        if (!contactInGroup) {
            await contactsClient.addContactGroup(teamId, ouContactId.value, { group_id: groupId });
        }

        prefillContact(ouContactId.value, groupId, {
            team_id: teamId,
            group_id: groupId,
            id: ouContactId.value,
        });
        await mountConversation(undefined, groupId);
        loadingGroupSelection.value = false;
    } catch (e) {
        loadingGroupSelection.value = false;
        if (!axios.isCancel(e)) {
            $toasted.error(catchRequestError(e));
        }
        ouGroupSelectionVisible.value = true;
    }
};

const onGroupSelectionCancel = () => {
    ouGroupSelectionVisible.value = false;
    ouContactId.value = null;
    ouContactGroups.value = [];
    ouSelectedGroupId.value = null;
    unmount();
};

const handleUnloadContact = () => {
    unmount();
    if (currentOrgUnit.value?.id && ouContactId.value) {
        prefillContact(ouContactId.value, undefined);
        ouGroupSelectionVisible.value = true;
    }
};

useEventListener(document, 'VoxieExtension:PhoneNumberDetected', async (event) => {
    const phoneNumber = e164Format(event?.detail?.phoneNumber);

    if (!phoneNumber) {
        return;
    }

    if (queryParams.phoneNumber) {
        queryParams.phoneNumber = phoneNumber;
    } else {
        queryParams.action = 'VoxieExtension:PhoneNumberDetected';
        queryParams.data = phoneNumber;
    }

    loadContact(phoneNumber);
});

useEventListener(document, 'VoxieExtension:ChangeGroup', async () => {
    changeGroupModal.value = true;
});

onMounted(async () => {
    mountMessageHub();

    if (!queryParams.phoneNumber && !queryParams.action) {
        return;
    }

    if (queryParams.action === 'VoxieExtension:ChangeGroup') {
        changeGroupModal.value = true;

        // Do not show change group on next load
        //  since query params will be stored always unless we clear them or change.
        queryParams.action = undefined;
    }

    if (
        (queryParams.phoneNumber && !e164Format(queryParams.phoneNumber)) ||
        (queryParams.action === 'VoxieExtension:PhoneNumberDetected' && !e164Format(queryParams.data))
    ) {
        return;
    } else {
        const phoneNumber = e164Format(queryParams.phoneNumber || queryParams.data);
        loadContact(phoneNumber);
    }
});
</script>
<style>
.sheet-enter-active,
.sheet-leave-active {
    transition: opacity 0.3s ease;
}
.sheet-enter-active > :last-child,
.sheet-leave-active > :last-child {
    transition: transform 0.3s ease;
}
.sheet-enter-from,
.sheet-leave-to {
    opacity: 0;
}
.sheet-enter-from > :last-child,
.sheet-leave-to > :last-child {
    transform: translateY(100%);
}
</style>
